import React, { useState, useEffect } from "react";
import { InfoTip } from "@/components/InfoTip";
import { Link } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useBilling } from "@/contexts/BillingContext";
import { toast } from "sonner";
import { BILLING } from "@/constants/testIds";
import { Sparkles, Loader2, ShieldCheck, KeyRound, Eye, EyeOff, Music2, VolumeX, BookOpen } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ShareApp } from "@/components/ShareApp";
import { api } from "@/lib/api";
import { MyInvoices } from "@/components/MyInvoices";
import { DefaultLevel } from "@/components/DefaultLevel";
import { useL } from "@/lib/useL";

function PasswordField({ value, onChange, label, testid, autoComplete }) {
  const L = useL();
  const [show, setShow] = useState(false);
  return (
    <label className="block">
      <span className="sux-marker mb-1 block">{label}</span>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          autoComplete={autoComplete}
          className="w-full bg-transparent border border-border pl-4 pr-11 py-3 font-mono text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          tabIndex={-1}
          data-testid={testid + "-toggle"}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground transition-colors"
          title={show ? L("Masquer", "Hide") : L("Afficher", "Show")}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </label>
  );
}

function ChangePasswordCard() {
  const L = useL();
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [saving, setSaving] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (newPwd.length < 6) {
      toast.error(L("Le nouveau mot de passe doit contenir au moins 6 caractères.", "The new password must be at least 6 characters long."));
      return;
    }
    if (newPwd !== confirmPwd) {
      toast.error(L("Les deux nouveaux mots de passe ne correspondent pas.", "The two new passwords do not match."));
      return;
    }
    if (newPwd === currentPwd) {
      toast.error(L("Le nouveau mot de passe doit être différent de l'ancien.", "The new password must be different from the old one."));
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPwd,
        new_password: newPwd,
      });
      toast.success(L("Mot de passe modifié avec succès.", "Password changed successfully."));
      setCurrentPwd("");
      setNewPwd("");
      setConfirmPwd("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || L("Erreur lors du changement de mot de passe.", "Error while changing the password."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-10 max-w-3xl border border-border p-6 bg-secondary/30">
      <div className="sux-marker mb-2 flex items-center gap-2">
        <KeyRound className="w-3 h-3" /> {L("Sécurité", "Security")} <InfoTip testid="settings-security-info" content={L("Changez votre mot de passe ici. Si vous l'avez oublié, déconnectez-vous et cliquez « Mot de passe oublié ? » sur la page de connexion.", "Change your password here. If you forgot it, log out and click « Forgot password? » on the login page.")} />
      </div>
      <h4 className="text-base font-black mb-1" style={{ fontFamily: "Outfit" }}>
        {L("Changer mon mot de passe", "Change my password")}
      </h4>
      <p className="text-sm text-muted-foreground mb-5">
        {L("Choisissez un mot de passe unique d'au moins 6 caractères.", "Choose a unique password of at least 6 characters.")}
      </p>
      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <PasswordField
            label={L("Mot de passe actuel", "Current password")}
            value={currentPwd}
            onChange={setCurrentPwd}
            testid="settings-current-password"
            autoComplete="current-password"
          />
        </div>
        <PasswordField
          label={L("Nouveau mot de passe", "New password")}
          value={newPwd}
          onChange={setNewPwd}
          testid="settings-new-password"
          autoComplete="new-password"
        />
        <PasswordField
          label={L("Confirmer le nouveau", "Confirm new password")}
          value={confirmPwd}
          onChange={setConfirmPwd}
          testid="settings-confirm-password"
          autoComplete="new-password"
        />
        <div className="md:col-span-2">
          <button
            type="submit"
            disabled={saving || !currentPwd || !newPwd || !confirmPwd}
            data-testid="settings-change-password-submit"
            className="bg-[#002FA7] text-white font-mono uppercase tracking-[0.2em] text-[11px] px-5 py-3 hover:bg-[#00227A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" /> {L("Enregistrement…", "Saving…")}
              </>
            ) : (
              <>
                <KeyRound className="w-3 h-3" /> {L("Modifier le mot de passe", "Change password")}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Settings() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { billing, startCheckout, paymentsBlockedInTWA } = useBilling();
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [splashSoundOn, setSplashSoundOn] = useState(() => {
    try {
      return localStorage.getItem("sux1-splash-sound") !== "off";
    } catch (e) {
      return true;
    }
  });
  const isFr = i18n.resolvedLanguage === "fr";

  const toggleSplashSound = () => {
    const next = !splashSoundOn;
    setSplashSoundOn(next);
    try {
      localStorage.setItem("sux1-splash-sound", next ? "on" : "off");
    } catch (e) { /* ignore */ }
    toast.success(
      next
        ? (isFr ? "Cloche cristalline activée à l'ouverture." : "Crystal bell enabled on launch.")
        : (isFr ? "Cloche cristalline désactivée." : "Crystal bell disabled.")
    );
  };

  useEffect(() => {
    if (!user) return;
    api
      .get("/admin/me")
      .then((res) => setIsAdmin(!!res.data?.is_admin))
      .catch(() => setIsAdmin(false));
  }, [user]);
  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const dateLocale = i18n.resolvedLanguage === "fr" ? "fr-FR" : "en-US";

  return (
    <div className="border-t border-border grid grid-cols-1 md:grid-cols-12">
      <aside className="md:col-span-3 border-r border-b border-border p-8">
        <div className="sux-marker mb-3">{t("settings.marker")}</div>
        <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
          {t("settings.title")}
        </h2>
        <p className="text-muted-foreground text-sm mt-3">{t("settings.subtitle")}</p>
      </aside>

      <section className="md:col-span-9 p-8 lg:p-12 border-b border-border">
        <div className="flex items-center gap-6 mb-10">
          <div className="w-20 h-20 bg-[#002FA7] text-white flex items-center justify-center font-black text-2xl" style={{ fontFamily: "Outfit" }}>
            {initials || "S"}
          </div>
          <div>
            <div className="sux-marker mb-1">{t("settings.profile")}</div>
            <h3 className="text-2xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>{user?.name}</h3>
            <p className="text-muted-foreground font-mono text-sm">{user?.email}</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-border max-w-3xl">
          <div className="p-6 border-r border-b border-border">
            <div className="sux-marker mb-2">{t("settings.member_since")}</div>
            <div className="font-mono text-sm">
              {user?.created_at
                ? new Date(user.created_at).toLocaleDateString(dateLocale, {
                    year: "numeric",
                    month: "long",
                    day: "2-digit",
                  })
                : "—"}
            </div>
          </div>
          <div className="p-6 border-b border-border">
            <div className="sux-marker mb-2 flex items-center gap-1">{t("settings.plan")} <InfoTip testid="settings-plan-info" content={t("tips.plan", "1 crédit = 1 résumé avec Gemini 3 Flash (jusqu'à environ 20 pages). Claude et GPT : 2 crédits. Les crédits n'expirent jamais.")} /></div>
            {billing?.is_pro ? (
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm">{t("settings.pro_plan")}</span>
                <span className="inline-flex items-center gap-1 bg-[#002FA7] text-white px-2 py-0.5 font-mono uppercase tracking-[0.15em] text-[9px]">
                  <Sparkles className="w-2.5 h-2.5" /> {t("nav.pro")}
                </span>
              </div>
            ) : billing?.is_admin ? (
              <div className="font-mono text-sm" data-testid="settings-admin-plan">{t("settings.admin_plan", "Illimité (admin)")}</div>
            ) : (billing?.bonus_summaries || 0) > 0 || billing?.has_paid ? (
              <div className="font-mono text-sm" data-testid="settings-credits-plan">
                {t("settings.credits_plan", { count: billing?.bonus_summaries || 0, defaultValue: "Packs de crédits · {{count}} crédit(s) disponible(s)" })}
              </div>
            ) : (
              <div className="font-mono text-sm">
                {t("settings.free_plan", {
                  used: billing?.free_summaries_used ?? 0,
                  limit: billing?.free_summary_limit ?? 3,
                })}
              </div>
            )}
          </div>
          <div className="p-6 border-r border-border">
            <div className="sux-marker mb-2 flex items-center gap-1">{t("settings.models")} <InfoTip testid="settings-models-info" content={t("tips.models", "Les 3 IA disponibles. Vous choisissez au moment du résumé. Gemini 3 Flash est rapide et économique ; Claude et GPT sont plus détaillés.")} /></div>
            <div className="font-mono text-sm">Claude Sonnet 4.5 · GPT-5.2 · Gemini 3 Flash</div>
          </div>
          <div className="p-6">
            <div className="sux-marker mb-2 flex items-center gap-1">{t("settings.sources")} <InfoTip testid="settings-sources-info" content={t("tips.sources", "Ce que SUX1 sait lire : texte collé, fichiers PDF, Word, EPUB, photos, pages web et photos de livres.")} /></div>
            <div className="font-mono text-sm">{t("settings.sources_value")}</div>
          </div>
        </div>

        <div className="mt-10 max-w-3xl">
          <LanguageSwitcher variant="settings" />
        </div>

        <DefaultLevel />
        <NotificationToggles />
        {!user?.is_anonymous && <MyInvoices />}

        <div className="mt-6 max-w-3xl border border-border p-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <BookOpen className="w-5 h-5 text-[#002FA7] mt-0.5 flex-shrink-0" />
            <div>
              <div className="sux-marker mb-1">
                {isFr ? "GUIDE D'UTILISATION" : "USER GUIDE"}
              </div>
              <div className="text-sm text-muted-foreground leading-relaxed max-w-md">
                {isFr
                  ? "Tuto complet (2 pages, imprimable en PDF) : créer son compte, ajouter un texte, paramétrer un résumé, cas d'usage enseignants et étudiants."
                  : "Full tutorial (2 pages, printable PDF): create an account, add text, configure a summary, teacher and student use cases."}
              </div>
            </div>
          </div>
          <a
            href={isFr ? "/guide.html" : "/guide-en.html"}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="settings-guide-link"
            className="font-mono uppercase tracking-[0.2em] text-[11px] px-5 py-3 bg-[#002FA7] text-white hover:bg-[#00227A] transition-colors"
          >
            {isFr ? "Ouvrir le guide" : "Open the guide"}
          </a>
        </div>

        <div className="mt-6 max-w-3xl border border-border p-6 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            {splashSoundOn ? (
              <Music2 className="w-5 h-5 text-[#002FA7] mt-0.5 flex-shrink-0" />
            ) : (
              <VolumeX className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            )}
            <div>
              <div className="sux-marker mb-1">
                {isFr ? "CLOCHE CRISTALLINE À L'OUVERTURE" : "CRYSTAL BELL ON LAUNCH"}
              </div>
              <div className="text-sm text-muted-foreground leading-relaxed max-w-md">
                {isFr
                  ? "Deux notes de cloche cristalline retentissent quand vous ouvrez SUX1."
                  : "Two crystalline bell chimes ring when you launch SUX1."}
              </div>
            </div>
          </div>
          <button
            onClick={toggleSplashSound}
            data-testid="settings-splash-sound-toggle"
            className={`font-mono uppercase tracking-[0.2em] text-[11px] px-5 py-3 transition-colors ${
              splashSoundOn
                ? "bg-[#002FA7] text-white hover:bg-[#00227A]"
                : "bg-card text-foreground border border-border hover:bg-[#FF2A00] hover:text-white"
            }`}
          >
            {splashSoundOn
              ? (isFr ? "Activée" : "On")
              : (isFr ? "Désactivée" : "Off")}
          </button>
        </div>

        <div className="mt-10 max-w-3xl border border-[#FF2A00]/30 p-6 bg-[#FF2A00]/5">
          <div className="sux-marker mb-2 text-[#FF2A00]">{t("settings.tip_marker")}</div>
          <p className="text-sm leading-relaxed">
            <Trans
              i18nKey="settings.tip_body"
              components={{ bold: <span className="font-mono font-semibold" /> }}
            />
          </p>
        </div>

        {!billing?.is_pro && (
          <div className="mt-6 max-w-3xl border border-[#002FA7] p-6 flex items-center justify-between gap-4 flex-wrap">
            <div>
              <div className="sux-marker mb-1">{t("nav.upgrade")}</div>
              <div className="text-base font-medium">
                {paymentsBlockedInTWA
                  ? t("billing.upgrade_twa_notice_title")
                  : t("settings.upgrade_now")}
              </div>
            </div>
            <button
              onClick={async () => {
                setLoading(true);
                try {
                  await startCheckout();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || t("billing.checkout_failed"));
                  setLoading(false);
                }
              }}
              disabled={loading}
              data-testid={BILLING.upgradeBtn}
              className="font-mono uppercase tracking-[0.25em] text-[11px] bg-[#002FA7] text-white px-5 py-3 hover:bg-[#00227A] transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-3 h-3 animate-spin" /> {t("billing.paywall_redirecting")}
                </>
              ) : (
                <>
                  <Sparkles className="w-3 h-3" /> {t("nav.upgrade")}
                </>
              )}
            </button>
          </div>
        )}

        {/* Share SUX1 to social networks */}
        <div className="mt-10 max-w-3xl">
          <ShareApp />
        </div>

        <ChangePasswordCard />

        {isAdmin && (
          <div className="mt-10 max-w-3xl border border-[#002FA7] p-6 bg-[#002FA7]/5">
            <div className="sux-marker mb-2 text-[#002FA7] flex items-center gap-2">
              <ShieldCheck className="w-3 h-3" /> Admin
            </div>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h4 className="text-base font-black" style={{ fontFamily: "Outfit" }}>
                  Panneau admin
                </h4>
                <p className="text-sm text-muted-foreground mt-1">
                  Accorder le Pass Pro à vie à vos beta-testeurs Google Play.
                </p>
              </div>
              <Link
                to="/admin"
                data-testid="settings-admin-link"
                className="font-mono uppercase tracking-[0.2em] text-[11px] bg-[#002FA7] text-white px-4 py-3 hover:bg-[#00227A] transition-colors flex items-center gap-2"
              >
                <ShieldCheck className="w-3 h-3" /> Ouvrir
              </Link>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * User-facing mute toggles for the "résumé prêt" voice announcement
 * and the crystal-bell chime played after each successful operation.
 * State persisted in localStorage — no backend round-trip needed.
 */
const PRESETS_FR = ["Résumé prêt", "Votre synthèse est prête", "Terminé", "Voilà, c'est prêt", "Résumé terminé, à vous", "Synthèse disponible", "C'est fini"];
const PRESETS_EN = ["Summary ready", "Your summary is ready", "Done", "All done", "Summary complete", "Ready for you"];

function NotificationToggles() {
  const L = useL();
  const PRESETS = L(PRESETS_FR, PRESETS_EN);
  const DEFAULT = PRESETS[0];
  const [soundMuted, setSoundMuted] = React.useState(false);
  const [voiceMuted, setVoiceMuted] = React.useState(false);
  const [phrase, setPhrase] = React.useState(DEFAULT);
  const [customPhrase, setCustomPhrase] = React.useState("");

  React.useEffect(() => {
    try {
      setSoundMuted(localStorage.getItem("sux1_sound_muted") === "1");
      setVoiceMuted(localStorage.getItem("sux1_voice_muted") === "1");
      const saved = localStorage.getItem("sux1_voice_phrase") || DEFAULT;
      if (PRESETS.includes(saved)) {
        setPhrase(saved);
      } else {
        setPhrase("__custom__");
        setCustomPhrase(saved);
      }
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [DEFAULT]);

  const persist = (key, value) => {
    try {
      if (value === "" || value === null || value === false) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, value === true ? "1" : String(value));
      }
    } catch (_) {}
  };

  const applyPhrase = (next) => {
    setPhrase(next);
    if (next === "__custom__") {
      // custom mode: keep whatever's already typed
      persist("sux1_voice_phrase", customPhrase.trim() || DEFAULT);
    } else {
      persist("sux1_voice_phrase", next);
    }
  };

  const preview = async () => {
    const mod = await import("@/lib/soundNotifications");
    mod.notifyOperationComplete(true);
  };

  return (
    <div className="mt-6 max-w-3xl border border-border p-6" data-testid="settings-notifications">
      <div className="sux-marker mb-4">{L("Notifications sonores", "Sound notifications")}</div>
      <div className="space-y-4">
        <label className="flex items-center justify-between gap-4 cursor-pointer">
          <div>
            <div className="font-mono text-sm">{L("Son cristallin", "Crystal chime")}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {L("Joué à la fin de chaque opération (résumé, copie, extraction…)", "Played when each operation ends (summary, copy, extraction…)")}
            </div>
          </div>
          <input
            type="checkbox"
            checked={!soundMuted}
            onChange={(e) => {
              const off = !e.target.checked;
              setSoundMuted(off);
              persist("sux1_sound_muted", off ? true : false);
            }}
            data-testid="settings-toggle-sound"
            className="w-5 h-5 accent-[#002FA7]"
          />
        </label>
        <label className="flex items-center justify-between gap-4 cursor-pointer">
          <div>
            <div className="font-mono text-sm">{L("Voix", "Voice")}</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {L("Une voix annonce que le résumé est prêt (voix de votre appareil, gratuite)", "A voice says the summary is ready (your device voice, free)")}
            </div>
          </div>
          <input
            type="checkbox"
            checked={!voiceMuted}
            onChange={(e) => {
              const off = !e.target.checked;
              setVoiceMuted(off);
              persist("sux1_voice_muted", off ? true : false);
            }}
            data-testid="settings-toggle-voice"
            className="w-5 h-5 accent-[#002FA7]"
          />
        </label>

        {/* Phrase selector — hidden when voice is muted */}
        {!voiceMuted && (
          <div className="pt-3 border-t border-border/50">
            <div className="font-mono text-sm mb-1">{L("Phrase d'annonce", "Announcement phrase")}</div>
            <div className="text-xs text-muted-foreground mb-3">
              {L("Choisissez ce que dit la voix quand un résumé est prêt.", "Choose what the voice says when a summary is ready.")}
            </div>
            <select
              value={phrase}
              onChange={(e) => applyPhrase(e.target.value)}
              data-testid="settings-voice-phrase-select"
              className="w-full border border-border bg-background px-3 py-2 font-mono text-sm"
            >
              {PRESETS.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
              <option value="__custom__">{L("Phrase personnalisée…", "Custom phrase…")}</option>
            </select>
            {phrase === "__custom__" && (
              <input
                type="text"
                value={customPhrase}
                onChange={(e) => {
                  setCustomPhrase(e.target.value);
                  persist("sux1_voice_phrase", e.target.value.trim() || DEFAULT);
                }}
                maxLength={60}
                placeholder={L("Ex : Votre synthèse est disponible", "E.g. Your summary is available")}
                data-testid="settings-voice-phrase-custom"
                className="mt-2 w-full border border-border bg-background px-3 py-2 font-mono text-sm"
              />
            )}
          </div>
        )}

        <div className="pt-3 border-t border-border/50 flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            {L("Tester maintenant : son + voix", "Test now: sound + voice")}
          </div>
          <button
            type="button"
            onClick={preview}
            data-testid="settings-preview-notification"
            className="font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 border border-border hover:bg-[#FF2A00] hover:text-white transition-colors"
          >
            {L("Écouter", "Listen")}
          </button>
        </div>
      </div>
    </div>
  );
}

